/**
 * Уведомления платформы и службы компании (миграция 0259).
 *
 * Лента общая и не зависит от прав на агентов: бухгалтер без доступа к
 * «Юристу» должен увидеть, что подписанный договор передан ему в работу.
 */

/** Уведомление в личной ленте сотрудника. */
export interface PlatformNotification {
  id: string;
  recipient_id: string;
  /** Вид события: `contract_handoff` — ввод договора в работу. */
  kind: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  body: string;
  /** Объект уведомления: `contract_handoff` + id передачи договора службе. */
  ref_type: string | null;
  ref_id: string | null;
  created_at: string;
  read_at: string | null;
  /** Передача договора службе — по ней «Принял в работу» прямо из ленты. */
  handoff_id: string | null;
  /** Договор передачи: карточка открывается тем, у кого есть «Юрист». */
  handoff_contract_id: string | null;
  /** Служба уже приняла договор: когда и кто (может быть коллега по службе). */
  handoff_acknowledged_at: string | null;
  handoff_acknowledged_by_name: string | null;
  handoff_ack_note?: string | null;
}

/** Виды обязательств из условий исполнения договора — их распределяют по службам. */
export type ExecRole = 'accounting' | 'mto' | 'pto' | 'field' | 'legal' | 'tender' | 'other';

/** Служба компании — получатель уведомлений по договорам. */
export interface OrgService {
  id: string;
  title: string;
  /** Какие обязательства ведёт служба. */
  exec_roles: ExecRole[];
  /** Уведомлять обо всех договорах, даже без своих обязательств. */
  always_notify: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  /** Версия для защиты от затирания чужой правки. */
  version: number;
}

export interface OrgServiceMember {
  service_id: string;
  user_id: string;
  full_name: string;
  email: string;
  department: string | null;
  added_at: string;
}

export interface OrgServicesResponse {
  items: OrgService[];
  members: OrgServiceMember[];
  exec_roles: ExecRole[];
}

export interface OrgServiceBody {
  title: string;
  exec_roles: ExecRole[];
  always_notify: boolean;
  is_active?: boolean;
  sort_order?: number;
  version?: number;
}

/** Одно поручение службе на момент передачи договора. */
export interface HandoffDuty {
  title: string;
  clause: string | null;
  /** Срок словами: «3 дня после подписания договора». */
  due_hint: string | null;
}

/** Передача договора службе. */
export interface ContractHandoff {
  id: string;
  contract_id: string;
  service_id: string;
  service_title: string;
  duties: HandoffDuty[];
  /** Скольким людям ушло уведомление. Ноль — в службе никого нет. */
  notified_count: number;
  created_at: string;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  acknowledged_at: string | null;
  ack_note: string | null;
}

/** Итог нажатия «Ввести в работу». */
export interface HandoffResult {
  services: number;
  notified: number;
  /** Службы без людей: передача записана, но читать её некому. */
  empty_services: string[];
}

/** Подписи видов обязательств — те же слова, что в карточке договора. */
export const EXEC_ROLE_LABELS: Record<string, string> = {
  accounting: 'Оплата и учёт',
  mto: 'Поставка и снабжение',
  pto: 'Техническая документация',
  field: 'Работы и приёмка',
  legal: 'Юридические сроки',
  tender: 'Тендеры',
  other: 'Прочее',
};

export function execRoleLabel(role: string): string {
  return EXEC_ROLE_LABELS[role] ?? role;
}
